import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

import {
  CheckCircle2,
  Upload,
  FileText,
  Image as ImageIcon,
  Building2,
  CreditCard,
  FileCheck2,
  RefreshCw,
  Eye,
  AlertCircle,
  MapPin,
  Mail,
  Phone,
  Globe,
  CalendarDays,
  BriefcaseBusiness,
  X,
} from "lucide-react";
import PartnerFooter from "../../component/PartnerFooter";
import PartnerHeader from "../../component/PartnerHeader";

const API_BASE = "/api";

const PRIMARY = "#3E0F8D";
const SECONDARY = "#9564DD";
const ACCENT = "#E4DA72";
const BG = "#F7F7FB";

// ============================================================
// DOCUMENT CONFIGURATION
// ============================================================

const DOCUMENTS = [
  {
    key: "logo",
    title: "Company Logo",
    description: "Upload your official company logo.",
    icon: ImageIcon,
    accept: "image/png,image/jpeg,image/jpg,image/webp",
    uploadPath: (partnerId) =>
      `/company-details/partner/${partnerId}/logo`,
    viewPath: (partnerId) =>
      `/company-details/partner/${partnerId}/logo`,
    fileField: "logoFileName",
    color: "#3E0F8D",
  },

  {
    key: "gst",
    title: "GST Certificate",
    description: "Upload the company's GST certificate.",
    icon: CreditCard,
    accept:
      "application/pdf,image/jpeg,image/jpg,image/png",
    uploadPath: (partnerId) =>
      `/company-details/partner/${partnerId}/gst`,
    viewPath: (partnerId) =>
      `/company-details/partner/${partnerId}/gst`,
    fileField: "gstCertificateFileName",
    color: "#2563EB",
  },

  {
    key: "pan",
    title: "PAN Card",
    description: "Upload the company's PAN card.",
    icon: FileText,
    accept:
      "application/pdf,image/jpeg,image/jpg,image/png",
    uploadPath: (partnerId) =>
      `/company-details/partner/${partnerId}/pan`,
    viewPath: (partnerId) =>
      `/company-details/partner/${partnerId}/pan`,
    fileField: "panCardFileName",
    color: "#059669",
  },

  {
    key: "registration",
    title: "Company Registration",
    description:
      "Upload your company registration document.",
    icon: FileCheck2,
    accept:
      "application/pdf,image/jpeg,image/jpg,image/png",
    uploadPath: (partnerId) =>
      `/company-details/partner/${partnerId}/registration-document`,
    viewPath: (partnerId) =>
      `/company-details/partner/${partnerId}/registration-document`,
    fileField: "companyRegistrationFileName",
    color: "#D97706",
  },
];

// ============================================================
// GET LOGGED-IN USER
// ============================================================

const getStoredUser = () => {
  try {
    const possibleKeys = [
      "user",
      "currentUser",
      "loggedInUser",
      "authUser",
    ];

    for (const key of possibleKeys) {
      const value = localStorage.getItem(key);

      if (!value) {
        continue;
      }

      try {
        const parsed = JSON.parse(value);

        if (parsed) {
          return parsed;
        }
      } catch {
        // Continue checking other keys.
      }
    }

    return null;
  } catch (error) {
    console.error(
      "Unable to read logged-in user:",
      error
    );

    return null;
  }
};

// ============================================================
// DATE FORMAT
// ============================================================

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  try {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

// ============================================================
// FILE EXTENSION
// ============================================================

const getFileExtension = (fileName = "") => {
  const parts = fileName.split(".");

  if (parts.length <= 1) {
    return "FILE";
  }

  return parts[parts.length - 1].toUpperCase();
};

// ============================================================
// FILE SIZE
// ============================================================

const formatFileSize = (bytes) => {
  if (!bytes) {
    return "";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function PartnerVerification() {
  const [partner, setPartner] = useState(null);

  const [companyDetails, setCompanyDetails] =
    useState(null);

  const [loading, setLoading] = useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [uploadingKey, setUploadingKey] =
    useState(null);

  const [selectedFiles, setSelectedFiles] =
    useState({});

  const [previewUrl, setPreviewUrl] =
    useState(null);

  const [previewTitle, setPreviewTitle] =
    useState("");

  const [previewLoading, setPreviewLoading] =
    useState(false);

  const [error, setError] = useState("");

  const currentUser = useMemo(() => {
    return getStoredUser();
  }, []);

  // ==========================================================
  // LOAD PARTNER + COMPANY DETAILS
  // ==========================================================

  const loadData = async (
    showRefreshLoader = false
  ) => {
    try {
      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const user = getStoredUser();

      if (!user?.email) {
        throw new Error(
          "Logged-in user information was not found. Please login again."
        );
      }

      // ------------------------------------------------------
      // GET PARTNERS
      // ------------------------------------------------------

      const partnersResponse = await fetch(
        `${API_BASE}/partners/all`
      );

      if (!partnersResponse.ok) {
        throw new Error(
          `Unable to load partners. HTTP ${partnersResponse.status}`
        );
      }

      const partnersData =
        await partnersResponse.json();

      const partners = Array.isArray(
        partnersData
      )
        ? partnersData
        : [];

      // ------------------------------------------------------
      // FIND LOGGED-IN PARTNER
      // ------------------------------------------------------

      const foundPartner = partners.find(
        (item) =>
          item?.email?.toLowerCase() ===
            user.email.toLowerCase() ||
          item?.officialEmail?.toLowerCase() ===
            user.email.toLowerCase()
      );

      if (!foundPartner?.id) {
        throw new Error(
          "Partner profile was not found for the logged-in account."
        );
      }

      setPartner(foundPartner);

      // ------------------------------------------------------
      // GET COMPANY DETAILS
      // ------------------------------------------------------

      const companyResponse = await fetch(
        `${API_BASE}/company-details/partner/${foundPartner.id}`
      );

      if (companyResponse.status === 404) {
        setCompanyDetails(null);
        return;
      }

      if (!companyResponse.ok) {
        throw new Error(
          `Unable to load company details. HTTP ${companyResponse.status}`
        );
      }

      const companyData =
        await companyResponse.json();

      setCompanyDetails(
        companyData || null
      );
    } catch (error) {
      console.error(
        "Partner verification load error:",
        error
      );

      setError(
        error?.message ||
          "Something went wrong while loading verification details."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    loadData();
  }, []);


    // ==========================================================
  // DOCUMENT INFO
  // ==========================================================

  const getDocumentInfo = (documentKey) => {
    if (!companyDetails) {
      return {
        fileName: "",
        uploadUrl: "",
        viewUrl: "",
      };
    }

    const document = DOCUMENTS.find(
      (item) => item.key === documentKey
    );

    if (!document) {
      return {
        fileName: "",
        uploadUrl: "",
        viewUrl: "",
      };
    }

    // Safe partner ID
    const partnerId =
      partner?.id ||
      companyDetails?.partnerId;

    if (!partnerId) {
      return {
        fileName: "",
        uploadUrl: "",
        viewUrl: "",
      };
    }

    return {
      fileName:
        companyDetails[document.fileField] || "",

      uploadUrl:
        document.uploadPath(partnerId),

      viewUrl:
        document.viewPath(partnerId),
    };
  };

  // ==========================================================
  // DOCUMENT STATUS
  // ==========================================================

  const getDocumentStatus = (documentKey) => {
    const info = getDocumentInfo(documentKey);

    return info.fileName
      ? "UPLOADED"
      : "NOT_UPLOADED";
  };

  // ==========================================================
  // UPLOADED DOCUMENT COUNT
  // ==========================================================

  const uploadedCount = useMemo(() => {
    return DOCUMENTS.filter(
      (document) =>
        getDocumentStatus(document.key) ===
        "UPLOADED"
    ).length;
  }, [companyDetails, partner]);

  // ==========================================================
  // PROGRESS
  // ==========================================================

  const progressPercentage = Math.round(
    (uploadedCount / DOCUMENTS.length) * 100
  );

  // ==========================================================
  // FILE SELECT
  // ==========================================================

  const handleFileChange = (
    documentKey,
    event
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    // Maximum 10 MB
    const MAX_SIZE = 10 * 1024 * 1024;

    if (file.size > MAX_SIZE) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: "Maximum allowed file size is 10 MB.",
        confirmButtonColor: PRIMARY,
      });

      event.target.value = "";
      return;
    }

    setSelectedFiles((previous) => ({
      ...previous,
      [documentKey]: file,
    }));
  };

  // ==========================================================
  // UPLOAD DOCUMENT
  // ==========================================================

  const handleUpload = async (documentKey) => {
    try {
      const file = selectedFiles[documentKey];

      if (!file) {
        Swal.fire({
          icon: "warning",
          title: "Select a File",
          text: "Please select a file before uploading.",
          confirmButtonColor: PRIMARY,
        });

        return;
      }

      const partnerId =
        partner?.id ||
        companyDetails?.partnerId;

      if (!partnerId) {
        throw new Error(
          "Partner ID is not available. Please refresh the page."
        );
      }

      const document = DOCUMENTS.find(
        (item) => item.key === documentKey
      );

      if (!document) {
        throw new Error(
          "Invalid document type."
        );
      }

      setUploadingKey(documentKey);

      // ======================================================
      // FORM DATA
      // ======================================================

      const formData = new FormData();

      // Backend expects:
      // @RequestParam("file")
      formData.append("file", file);

      // ======================================================
      // UPLOAD API
      // ======================================================

      const response = await fetch(
        `${API_BASE}${document.uploadPath(partnerId)}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        let message =
          `Upload failed. HTTP ${response.status}`;

        try {
          const errorData =
            await response.json();

          message =
            errorData?.message ||
            errorData?.error ||
            message;
        } catch {
          // Response is not JSON
        }

        throw new Error(message);
      }

      // Backend returns CompanyDetailsResponse
      const updatedCompanyDetails =
        await response.json();

      // Update UI immediately
      setCompanyDetails(
        updatedCompanyDetails
      );

      // Clear selected file
      setSelectedFiles((previous) => ({
        ...previous,
        [documentKey]: null,
      }));

      Swal.fire({
        icon: "success",
        title: "Uploaded Successfully",
        text: `${document.title} has been uploaded successfully.`,
        confirmButtonColor: PRIMARY,
      });

    } catch (error) {
      console.error(
        `Upload ${documentKey} error:`,
        error
      );

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          error?.message ||
          "Unable to upload the document.",
        confirmButtonColor: PRIMARY,
      });

    } finally {
      setUploadingKey(null);
    }
  };

  // ==========================================================
  // VIEW DOCUMENT
  // ==========================================================

  const handleViewDocument = async (
    documentKey
  ) => {
    try {
      const info =
        getDocumentInfo(documentKey);

      if (!info.viewUrl) {
        throw new Error(
          "Document URL is not available."
        );
      }

      if (!info.fileName) {
        Swal.fire({
          icon: "info",
          title: "Document Not Uploaded",
          text:
            "Please upload this document first.",
          confirmButtonColor: PRIMARY,
        });

        return;
      }

      setPreviewLoading(true);

      // ======================================================
      // GET DOCUMENT
      // ======================================================

      const response = await fetch(
        `${API_BASE}${info.viewUrl}`
      );

      if (!response.ok) {
        throw new Error(
          `Unable to open document. HTTP ${response.status}`
        );
      }

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error(
          "The server returned an empty document."
        );
      }

      // Create browser preview URL
      const objectUrl =
        URL.createObjectURL(blob);

      setPreviewUrl(objectUrl);
      setPreviewTitle(info.fileName);

    } catch (error) {
      console.error(
        `View ${documentKey} error:`,
        error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to Open",
        text:
          error?.message ||
          "Unable to open the document.",
        confirmButtonColor: PRIMARY,
      });

    } finally {
      setPreviewLoading(false);
    }
  };

  // ==========================================================
  // CLOSE PREVIEW
  // ==========================================================

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(null);
    setPreviewTitle("");
  };

  // ==========================================================
  // DOCUMENT CARD
  // ==========================================================

  const renderDocumentCard = (document) => {
    const Icon = document.icon;

    const info =
      getDocumentInfo(document.key);

    const status =
      getDocumentStatus(document.key);

    const selectedFile =
      selectedFiles[document.key];

    const isUploading =
      uploadingKey === document.key;

    return (
      <>
      
      <div
        key={document.key}
        className="verification-document-card"
      >

        {/* ==================================================
            DOCUMENT HEADER
        ================================================== */}

        <div className="document-card-header">

          <div
            className="document-icon"
            style={{
              background:
                `${document.color}12`,
              color: document.color,
            }}
          >
            <Icon size={24} />
          </div>

          <div className="document-header-content">

            <h3>
              {document.title}
            </h3>

            <span
              className={
                status === "UPLOADED"
                  ? "status-badge uploaded"
                  : "status-badge pending"
              }
            >

              {status === "UPLOADED" ? (
                <>
                  <CheckCircle2 size={14} />
                  Uploaded
                </>
              ) : (
                <>
                  <AlertCircle size={14} />
                  Not Uploaded
                </>
              )}

            </span>

          </div>

        </div>

        {/* ==================================================
            DESCRIPTION
        ================================================== */}

        <p className="document-description">
          {document.description}
        </p>

        {/* ==================================================
            EXISTING FILE
        ================================================== */}

        {info.fileName && (
          <div className="existing-file">

            <div className="existing-file-icon">
              <FileText size={18} />
            </div>

            <div className="existing-file-info">

              <strong title={info.fileName}>
                {info.fileName}
              </strong>

              <span>
                {getFileExtension(
                  info.fileName
                )}
              </span>

            </div>

            <button
              type="button"
              className="view-button"
              onClick={() =>
                handleViewDocument(
                  document.key
                )
              }
            >
              <Eye size={16} />
              View
            </button>

          </div>
        )}

        {/* ==================================================
            UPLOAD AREA
        ================================================== */}

        <div className="upload-area">

          <label
            htmlFor={`file-${document.key}`}
            className="file-input-label"
          >

            <Upload size={18} />

            <span>
              {selectedFile
                ? selectedFile.name
                : "Choose file"}
            </span>

          </label>

          <input
            id={`file-${document.key}`}
            type="file"
            accept={document.accept}
            onChange={(event) =>
              handleFileChange(
                document.key,
                event
              )
            }
          />

          {/* Selected File */}

          {selectedFile && (
            <div className="selected-file-info">

              <span>
                {selectedFile.name}
              </span>

              <small>
                {formatFileSize(
                  selectedFile.size
                )}
              </small>

            </div>
          )}

          {/* Upload Button */}

          <button
            type="button"
            className="upload-button"
            disabled={
              !selectedFile ||
              isUploading
            }
            onClick={() =>
              handleUpload(
                document.key
              )
            }
          >

            {isUploading ? (
              <>
                <span className="button-spinner" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={17} />

                {status === "UPLOADED"
                  ? "Replace Document"
                  : "Upload Document"}
              </>
            )}

          </button>

        </div>

        {/* ==================================================
            FILE LIMIT
        ================================================== */}

        <div className="supported-format">
          Maximum file size:{" "}
          <strong>10 MB</strong>
        </div>

      </div>
    
    </>
    );
  };



  // ==========================================================
  // LOADING SCREEN
  // ==========================================================

  if (loading) {
    return (
      <div className="verification-page">
        <style>{`
          .verification-page {
            min-height: 100vh;
            background: #F7F7FB;
            padding: 28px;
          }

          .loading-container {
            min-height: 70vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
          }

          .loading-container h3 {
            margin: 16px 0 6px;
            color: #1f2937;
            font-size: 18px;
          }

          .loading-container p {
            margin: 0;
            color: #6b7280;
            font-size: 13px;
          }

          .large-spinner {
            width: 42px;
            height: 42px;
            border: 4px solid #e5e7eb;
            border-top-color: #3E0F8D;
            border-radius: 50%;
            animation: verificationSpin 0.8s linear infinite;
          }

          @keyframes verificationSpin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>

        <div className="loading-container">
          <div className="large-spinner" />

          <h3>
            Loading Verification Details...
          </h3>

          <p>
            Please wait while your company information
            is being loaded.
          </p>
        </div>
      </div>
    );
  }

  // ==========================================================
  // ERROR SCREEN
  // ==========================================================

  if (error && !partner) {
    return (
      <div className="verification-page">
        <style>{`
          .verification-page {
            min-height: 100vh;
            background: #F7F7FB;
            padding: 28px;
          }

          .error-container {
            min-height: 70vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
          }

          .error-icon {
            width: 70px;
            height: 70px;
            border-radius: 50%;
            background: #fef2f2;
            color: #dc2626;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .error-container h2 {
            margin: 16px 0 7px;
            color: #1f2937;
          }

          .error-container p {
            max-width: 520px;
            color: #6b7280;
            font-size: 14px;
            line-height: 1.6;
          }

          .retry-button {
            margin-top: 15px;
            border: none;
            background: #3E0F8D;
            color: white;
            padding: 10px 17px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            gap: 7px;
            cursor: pointer;
            font-weight: 700;
          }

          .retry-button:hover {
            background: #32106f;
          }
        `}</style>

        <div className="error-container">

          <div className="error-icon">
            <AlertCircle size={35} />
          </div>

          <h2>
            Unable to Load Verification
          </h2>

          <p>
            {error}
          </p>

          <button
            type="button"
            className="retry-button"
            onClick={() => loadData()}
          >
            <RefreshCw size={17} />
            Try Again
          </button>

        </div>
      </div>
    );
  }

  // ==========================================================
  // MAIN PAGE
  // ==========================================================

  return (
    <>
      <PartnerHeader />
    <div className="verification-page">

      <style>{`

        * {
          box-sizing: border-box;
        }

        .verification-page {
          min-height: 100vh;
          background: #F7F7FB;
          padding: 28px;
          color: #1f2937;
        }

        .verification-container {
          max-width: 1250px;
          margin: 0 auto;
        }

        /* ==================================================
           PAGE HEADER
        ================================================== */

        .page-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 24px;
        }

        .page-title-wrapper {
          display: flex;
          align-items: flex-start;
          gap: 15px;
        }

        .page-title-icon {
          width: 52px;
          height: 52px;
          min-width: 52px;
          border-radius: 14px;
          background: #3E0F8D;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow:
            0 7px 18px rgba(62, 15, 141, 0.18);
        }

        .page-title-wrapper h1 {
          margin: 0;
          font-size: 27px;
          line-height: 1.2;
          font-weight: 750;
          color: #171717;
        }

        .page-title-wrapper p {
          margin: 7px 0 0;
          color: #6b7280;
          font-size: 14px;
        }

        .refresh-button {
          border: 1px solid #ddd6fe;
          background: white;
          color: #3E0F8D;
          height: 42px;
          padding: 0 15px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-weight: 650;
          transition: 0.2s ease;
        }

        .refresh-button:hover {
          background: #faf8ff;
          border-color: #c4b5fd;
        }

        .refresh-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* ==================================================
           COMPANY OVERVIEW
        ================================================== */

        .company-overview {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 22px;
          margin-bottom: 22px;
          box-shadow:
            0 3px 12px rgba(0, 0, 0, 0.035);
        }

        .overview-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
        }

        .company-heading {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }

        .company-logo-placeholder {
          width: 62px;
          height: 62px;
          min-width: 62px;
          border-radius: 14px;
          background: #f1edfb;
          color: #3E0F8D;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .company-heading h2 {
          margin: 0;
          font-size: 21px;
          line-height: 1.3;
          color: #111827;
          word-break: break-word;
        }

        .company-heading p {
          margin: 5px 0 0;
          color: #6b7280;
          font-size: 13px;
          word-break: break-word;
        }

        .company-status {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 12px;
          border-radius: 30px;
          background: #ecfdf5;
          color: #047857;
          font-size: 11px;
          font-weight: 750;
          text-transform: uppercase;
          white-space: nowrap;
        }

        /* ==================================================
           COMPANY INFO GRID
        ================================================== */

        .company-info-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
          margin-top: 22px;
          padding-top: 20px;
          border-top: 1px solid #f0f0f0;
        }

        .info-item {
          min-width: 0;
        }

        .info-label {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #9ca3af;
          font-size: 10px;
          text-transform: uppercase;
          font-weight: 750;
          letter-spacing: 0.35px;
          margin-bottom: 5px;
        }

        .info-value {
          color: #374151;
          font-size: 13px;
          line-height: 1.45;
          font-weight: 600;
          word-break: break-word;
        }

        /* ==================================================
           PROGRESS CARD
        ================================================== */

        .progress-card {
          background:
            linear-gradient(
              135deg,
              #3E0F8D,
              #9564DD
            );
          border-radius: 16px;
          padding: 22px;
          color: white;
          margin-bottom: 24px;
          box-shadow:
            0 8px 22px rgba(62, 15, 141, 0.18);
        }

        .progress-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 14px;
        }

        .progress-header h3 {
          margin: 0;
          font-size: 17px;
          font-weight: 750;
        }

        .progress-header p {
          margin: 4px 0 0;
          color: rgba(255,255,255,0.78);
          font-size: 12px;
        }

        .progress-percentage {
          font-size: 26px;
          font-weight: 800;
        }

        .progress-track {
          width: 100%;
          height: 9px;
          background: rgba(255,255,255,0.20);
          border-radius: 20px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: #E4DA72;
          border-radius: 20px;
          transition: width 0.45s ease;
        }

        /* ==================================================
           DOCUMENT HEADING
        ================================================== */

        .documents-heading {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
          margin-bottom: 14px;
        }

        .documents-heading h2 {
          margin: 0;
          font-size: 20px;
          color: #171717;
          font-weight: 750;
        }

        .documents-heading span {
          font-size: 12px;
          color: #6b7280;
        }

        /* ==================================================
           DOCUMENT GRID
        ================================================== */

        .documents-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 18px;
        }

        .verification-document-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 15px;
          padding: 19px;
          box-shadow:
            0 3px 10px rgba(0,0,0,0.025);
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        }

        .verification-document-card:hover {
          transform: translateY(-1px);
          box-shadow:
            0 7px 20px rgba(0,0,0,0.055);
        }

        .document-card-header {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .document-icon {
          width: 46px;
          height: 46px;
          min-width: 46px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .document-header-content {
          flex: 1;
          min-width: 0;
        }

        .document-header-content h3 {
          margin: 1px 0 6px;
          font-size: 16px;
          color: #1f2937;
          font-weight: 700;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          border-radius: 20px;
          padding: 4px 8px;
          font-size: 10px;
          font-weight: 750;
          text-transform: uppercase;
        }

        .status-badge.uploaded {
          background: #ecfdf5;
          color: #047857;
        }

        .status-badge.pending {
          background: #fff7ed;
          color: #c2410c;
        }

        .document-description {
          color: #6b7280;
          font-size: 12px;
          line-height: 1.55;
          margin: 14px 0;
        }

        /* ==================================================
           EXISTING FILE
        ================================================== */

        .existing-file {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 10px;
          background: #f8f8fb;
          border: 1px solid #eeeef2;
          border-radius: 9px;
          margin-bottom: 13px;
        }

        .existing-file-icon {
          width: 34px;
          height: 34px;
          min-width: 34px;
          border-radius: 7px;
          background: #eee9fa;
          color: #3E0F8D;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .existing-file-info {
          min-width: 0;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .existing-file-info strong {
          font-size: 11px;
          color: #374151;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .existing-file-info span {
          font-size: 9px;
          color: #9ca3af;
        }

        .view-button {
          border: 1px solid #ddd6fe;
          background: white;
          color: #3E0F8D;
          border-radius: 7px;
          padding: 6px 9px;
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          flex-shrink: 0;
        }

        .view-button:hover {
          background: #faf8ff;
        }

        /* ==================================================
           UPLOAD AREA
        ================================================== */

        .upload-area {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .file-input-label {
          flex: 1;
          min-width: 170px;
          height: 40px;
          border: 1px dashed #cfc9df;
          border-radius: 8px;
          padding: 0 11px;
          display: flex;
          align-items: center;
          gap: 7px;
          color: #6b7280;
          font-size: 11px;
          cursor: pointer;
          background: #fcfbff;
          transition: 0.2s ease;
        }

        .file-input-label:hover {
          border-color: #9564DD;
          background: #faf8ff;
        }

        .file-input-label span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .upload-area input[type="file"] {
          display: none;
        }

        .upload-button {
          height: 40px;
          padding: 0 13px;
          border: 0;
          border-radius: 8px;
          background: #3E0F8D;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
          transition: 0.2s ease;
        }

        .upload-button:hover:not(:disabled) {
          background: #32106f;
        }

        .upload-button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .button-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: white;
          border-radius: 50%;
          animation: verificationSpin 0.8s linear infinite;
        }

        .selected-file-info {
          width: 100%;
          display: flex;
          justify-content: space-between;
          gap: 10px;
          color: #6b7280;
          font-size: 10px;
          padding: 2px;
        }

        .selected-file-info span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .selected-file-info small {
          flex-shrink: 0;
        }

        .supported-format {
          color: #9ca3af;
          font-size: 10px;
          margin-top: 9px;
        }

        /* ==================================================
           PREVIEW MODAL
        ================================================== */

        .preview-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0,0,0,0.68);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 25px;
        }

        .preview-modal {
          width: min(1000px, 95vw);
          height: min(750px, 90vh);
          background: white;
          border-radius: 14px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow:
            0 20px 70px rgba(0,0,0,0.30);
        }

        .preview-header {
          min-height: 56px;
          padding: 0 15px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 15px;
          border-bottom: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .preview-header h3 {
          margin: 0;
          font-size: 14px;
          color: #1f2937;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .close-preview {
          width: 34px;
          height: 34px;
          min-width: 34px;
          border: 0;
          background: #f3f4f6;
          color: #374151;
          border-radius: 7px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-preview:hover {
          background: #e5e7eb;
        }

        .preview-body {
          flex: 1;
          min-height: 0;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .preview-body iframe {
          width: 100%;
          height: 100%;
          border: 0;
          background: white;
        }

        .preview-image {
          max-width: 100%;
          max-height: 100%;
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 20px;
        }

        /* ==================================================
           RESPONSIVE
        ================================================== */

        @media (max-width: 1050px) {

          .company-info-grid {
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
          }

        }

        @media (max-width: 850px) {

          .documents-grid {
            grid-template-columns: 1fr;
          }

        }

        @media (max-width: 650px) {

          .verification-page {
            padding: 15px;
          }

          .page-header {
            flex-direction: column;
          }

          .page-title-wrapper h1 {
            font-size: 23px;
          }

          .refresh-button {
            width: 100%;
            justify-content: center;
          }

          .overview-top {
            flex-direction: column;
          }

          .company-status {
            align-self: flex-start;
          }

          .company-info-grid {
            grid-template-columns: 1fr;
          }

          .progress-header {
            align-items: flex-start;
            flex-direction: column;
            gap: 8px;
          }

          .documents-heading {
            align-items: flex-start;
            flex-direction: column;
          }

          .upload-area {
            flex-direction: column;
            align-items: stretch;
          }

          .file-input-label {
            width: 100%;
          }

          .upload-button {
            width: 100%;
          }

          .existing-file {
            align-items: flex-start;
          }

          .preview-overlay {
            padding: 10px;
          }

          .preview-modal {
            width: 100%;
            height: 92vh;
          }

        }

      `}</style>

      <div className="verification-container">

        {/* ==================================================
            HEADER
        ================================================== */}

        <div className="page-header">

          <div className="page-title-wrapper">

            <div className="page-title-icon">
              <Building2 size={26} />
            </div>

            <div>

              <h1>
                Partner Verification
              </h1>

              <p>
                Manage your company information
                and verification documents.
              </p>

            </div>

          </div>

          <button
            type="button"
            className="refresh-button"
            onClick={() => loadData(true)}
            disabled={refreshing}
          >

            <RefreshCw
              size={17}
              style={{
                animation: refreshing
                  ? "verificationSpin 0.8s linear infinite"
                  : "none",
              }}
            />

            {refreshing
              ? "Refreshing..."
              : "Refresh"}

          </button>

        </div>

        {/* ==================================================
            COMPANY OVERVIEW
        ================================================== */}

        <div className="company-overview">

          <div className="overview-top">

            <div className="company-heading">

              <div className="company-logo-placeholder">

                {companyDetails?.logoFileName &&
                partner?.id ? (

                  <img
                    src={`${API_BASE}/company-details/partner/${partner.id}/logo`}
                    alt="Company Logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />

                ) : (

                  <Building2 size={30} />

                )}

              </div>

              <div>

                <h2>
                  {companyDetails?.companyDisplayName ||
                    companyDetails?.companyLegalName ||
                    partner?.companyName ||
                    partner?.name ||
                    "Company"}
                </h2>

                <p>
                  {companyDetails?.companyLegalName ||
                    "Company Details"}
                </p>

              </div>

            </div>

            <div className="company-status">

              <CheckCircle2 size={15} />

              {companyDetails?.status ||
                partner?.status ||
                "ACTIVE"}

            </div>

          </div>

          {/* ==================================================
              COMPANY INFORMATION
          ================================================== */}

          <div className="company-info-grid">

            <div className="info-item">

              <div className="info-label">
                <BriefcaseBusiness size={12} />
                Industry
              </div>

              <div className="info-value">
                {companyDetails?.industry || "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                <Mail size={12} />
                Official Email
              </div>

              <div className="info-value">
                {companyDetails?.officialEmail ||
                  partner?.email ||
                  currentUser?.email ||
                  "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                <Phone size={12} />
                Phone
              </div>

              <div className="info-value">
                {companyDetails?.officialPhone ||
                  partner?.mobile ||
                  partner?.phone ||
                  "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                <Globe size={12} />
                Website
              </div>

              <div className="info-value">
                {companyDetails?.officialWebsite ||
                  "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                <MapPin size={12} />
                City
              </div>

              <div className="info-value">
                {companyDetails?.city || "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                <MapPin size={12} />
                State
              </div>

              <div className="info-value">
                {companyDetails?.state || "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                <CreditCard size={12} />
                GST Number
              </div>

              <div className="info-value">
                {companyDetails?.gstNumber || "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                <FileText size={12} />
                PAN Number
              </div>

              <div className="info-value">
                {companyDetails?.panNumber || "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                <CalendarDays size={12} />
                Established
              </div>

              <div className="info-value">
                {companyDetails?.yearEstablished || "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                Company Size
              </div>

              <div className="info-value">
                {companyDetails?.companySize || "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                Registered Address
              </div>

              <div className="info-value">
                {companyDetails?.registeredAddress || "—"}
              </div>

            </div>

            <div className="info-item">

              <div className="info-label">
                Pincode
              </div>

              <div className="info-value">
                {companyDetails?.pincode || "—"}
              </div>

            </div>

          </div>

        </div>

        {/* ==================================================
            VERIFICATION PROGRESS
        ================================================== */}

        <div className="progress-card">

          <div className="progress-header">

            <div>

              <h3>
                Verification Progress
              </h3>

              <p>
                {uploadedCount} of{" "}
                {DOCUMENTS.length} documents uploaded
              </p>

            </div>

            <div className="progress-percentage">
              {progressPercentage}%
            </div>

          </div>

          <div className="progress-track">

            <div
              className="progress-fill"
              style={{
                width: `${progressPercentage}%`,
              }}
            />

          </div>

        </div>

        {/* ==================================================
            DOCUMENT SECTION HEADER
        ================================================== */}

        <div className="documents-heading">

          <h2>
            Verification Documents
          </h2>

          <span>
            Maximum file size: 10 MB
          </span>

        </div>

        {/* ==================================================
            DOCUMENT CARDS
        ================================================== */}

        <div className="documents-grid">

          {DOCUMENTS.map(
            renderDocumentCard
          )}

        </div>

      </div>

      {/* ==================================================
          DOCUMENT PREVIEW MODAL
      ================================================== */}

      {previewUrl && (

        <div
          className="preview-overlay"
          onClick={(event) => {

            if (
              event.target ===
              event.currentTarget
            ) {
              closePreview();
            }

          }}
        >

          <div className="preview-modal">

            <div className="preview-header">

              <h3>
                {previewTitle || "Document Preview"}
              </h3>

              <button
                type="button"
                className="close-preview"
                onClick={closePreview}
              >
                <X size={18} />
              </button>

            </div>

            <div className="preview-body">

              {previewTitle
                ?.toLowerCase()
                .match(
                  /\.(jpg|jpeg|png|gif|webp)$/i
                ) ? (

                <img
                  src={previewUrl}
                  alt={previewTitle}
                  className="preview-image"
                />

              ) : (

                <iframe
                  src={previewUrl}
                  title={previewTitle}
                />

              )}

            </div>

          </div>

        </div>

      )}

      {/* ==================================================
          PREVIEW LOADING
      ================================================== */}

      {previewLoading && (

        <div className="preview-overlay">

          <div className="large-spinner" />

        </div>

      )}

    </div>

    <PartnerFooter/>
    </>
  );
}
